import { AutoHoldType } from "../../enums/auto-hold-type.enum";
import BusinessTier from "../../enums/business-tier.enum";
import ClientTransitInfoDisplayLevel from "../../enums/client-transit-info-display-level.enum";
import ContractStatus from "../../enums/contract-status.enum";
import CostEnforcement from "../../enums/cost-enforcement.enum";
import DriverAgreementFlow from "../../enums/driver-agreement-flow.enum";
import { DefaultDriverPricingModel } from "../../enums/driver-pricing-model.enum";
import LoadSource from "../../enums/load-source.enum";
import LocationSource from "../../enums/location-source.enum";
import MissingInfoDisplayLevel from "../../enums/missing-info-display-level.enum";
import { NavigationSearchType } from "../../enums/navigation-search-type.enum";
import QuickPayMode from "../../enums/quick-pay-mode.enum";
import UserRole from "../../enums/user-role.enum";
import { UserRoleWildcard } from "../../enums/user-role-wildcard.enum";
import type { CreateOrganizationInput } from "../../types/organization.type";

export type FormFieldType =
  | "text"
  | "email"
  | "url"
  | "number"
  | "boolean"
  | "enum"
  | "stringList"
  | "enumList"
  | "nullableText"
  | "nullableNumber"
  | "nullableDate";

export type FormFieldDef = {
  key: keyof CreateOrganizationInput;
  label: string;
  type: FormFieldType;
  options?: string[];
  hint?: string;
  required?: boolean;
};

export type FormSection = {
  id: string;
  title: string;
  fields: FormFieldDef[];
};

function enumOptions(values: Record<string, string>): string[] {
  return Object.values(values);
}

const USER_ROLE_OPTIONS = enumOptions(UserRole);
const USER_ROLE_AND_WILDCARD_OPTIONS = [
  ...USER_ROLE_OPTIONS,
  ...enumOptions(UserRoleWildcard),
];

export const ORGANIZATION_FORM_SECTIONS: FormSection[] = [
  {
    id: "required",
    title: "Required",
    fields: [
      {
        key: "name",
        label: "Name (unique slug)",
        type: "text",
        required: true,
        hint: "Lowercase, no spaces, and no special characters.",
      },
      {
        key: "displayName",
        label: "Display name",
        type: "text",
        required: true,
      },
      // TODO make this a file upload?
      // {
      //   key: "driverAgreementFile",
      //   label: "Driver agreement file",
      //   type: "text",
      //   required: true,
      //   hint: "Required by the DB (no default).",
      // },
      { key: "hrEmail", label: "HR email", type: "email", required: true },
      {
        key: "lhNumberPrefix",
        label: "LH number prefix",
        type: "text",
        required: true,
        hint: "Must be unique to the organization.",
      },
    ],
  },
  {
    id: "identity",
    title: "Identity",
    fields: [
      { key: "mcNumber", label: "MC number", type: "nullableText" },
      {
        key: "tier",
        label: "Tier",
        type: "enum",
        options: enumOptions(BusinessTier),
      },
    ],
  },
  {
    id: "branding",
    title: "Branding",
    fields: [
      { key: "primaryColor", label: "Primary color", type: "nullableText" },
      { key: "secondaryColor", label: "Secondary color", type: "nullableText" },
      { key: "tabColor", label: "Tab color", type: "nullableText" },
      {
        key: "lblTruckInsuranceDescription",
        label: "Truck insurance description label",
        type: "text",
      },
    ],
  },
  {
    id: "contact",
    title: "Contact & support",
    fields: [
      {
        key: "loadhiveSupportEmail",
        label: "LoadHive support email",
        type: "email",
      },
      {
        key: "driverSupportEmail",
        label: "Driver support email",
        type: "email",
      },
      {
        key: "driverSupportPhone",
        label: "Driver support phone",
        type: "text",
      },
      {
        key: "trackingContactEmail",
        label: "Tracking contact email",
        type: "email",
      },
      {
        key: "trackingContactPhone",
        label: "Tracking contact phone",
        type: "text",
      },
      {
        key: "autoOfferReplyToEmail",
        label: "Auto-offer reply-to email",
        type: "email",
      },
      {
        key: "slackBusinessWebhook",
        label: "Slack business webhook",
        type: "nullableText",
      },
      {
        key: "emailTemplatesAssetUrl",
        label: "Email templates asset URL",
        type: "text",
      },
    ],
  },
  {
    id: "redirects",
    title: "Redirect URLs",
    fields: [
      {
        key: "redirectUrlPrivacyPolicy",
        label: "Privacy policy URL",
        type: "nullableText",
      },
      {
        key: "redirectUrlTermsOfService",
        label: "Terms of service URL",
        type: "nullableText",
      },
      { key: "redirectUrlEula", label: "EULA URL", type: "nullableText" },
      {
        key: "redirectUrlIosAppStore",
        label: "iOS App Store URL",
        type: "nullableText",
      },
      {
        key: "redirectUrlAndroidPlayStore",
        label: "Android Play Store URL",
        type: "nullableText",
      },
      {
        key: "redirectUrlDriverAgreement",
        label: "Driver agreement URL",
        type: "nullableText",
      },
      {
        key: "redirectUrlDriverAgreementPreview",
        label: "Driver agreement preview URL",
        type: "nullableText",
      },
    ],
  },
  {
    id: "client",
    title: "Client display",
    fields: [
      {
        key: "publicCreateLoadPageEnabled",
        label: "Public create load page enabled",
        type: "boolean",
      },
      {
        key: "publicCreateLoadPageAutoAssign",
        label: "Public create load page auto-assign",
        type: "boolean",
      },
      {
        key: "publicCreateLoadPageTripDistanceEnabled",
        label: "Public create load page trip distance",
        type: "boolean",
      },
      {
        key: "clientShowingLoadBooleans",
        label: "Client showing load booleans",
        type: "boolean",
      },
      {
        key: "clientShowingDriverEta",
        label: "Client showing driver ETA",
        type: "boolean",
      },
      {
        key: "clientShowPickupValues",
        label: "Client show pickup values",
        type: "boolean",
      },
      {
        key: "clientShowDeliverValues",
        label: "Client show deliver values",
        type: "boolean",
      },
      {
        key: "clientShowingPickupDetails",
        label: "Client showing pickup details",
        type: "enum",
        options: enumOptions(ClientTransitInfoDisplayLevel),
      },
      {
        key: "clientShowingDeliverDetails",
        label: "Client showing deliver details",
        type: "enum",
        options: enumOptions(ClientTransitInfoDisplayLevel),
      },
    ],
  },
  {
    id: "load-tier",
    title: "Load tier & sources",
    fields: [
      {
        key: "loadTierPremiumDomains",
        label: "Premium domains",
        type: "stringList",
        hint: "Comma-separated",
      },
      {
        key: "loadTierPremiumEmails",
        label: "Premium emails",
        type: "stringList",
        hint: "Comma-separated",
      },
      {
        key: "disabledLoadSources",
        label: "Disabled load sources",
        type: "enumList",
        options: enumOptions(LoadSource),
      },
      {
        key: "defaultLocationService",
        label: "Default location service",
        type: "enum",
        options: enumOptions(LocationSource),
      },
      {
        key: "navigationSearchType",
        label: "Navigation search type",
        type: "enum",
        options: enumOptions(NavigationSearchType),
      },
    ],
  },
  {
    id: "permissions",
    title: "Permissions & roles",
    fields: [
      {
        key: "allowEnableAutoAssignUserRoles",
        label: "Roles that can enable auto-assign",
        type: "enumList",
        options: USER_ROLE_AND_WILDCARD_OPTIONS,
      },
      {
        key: "allowDisableAutoAssignUserRoles",
        label: "Roles that can disable auto-assign",
        type: "enumList",
        options: USER_ROLE_AND_WILDCARD_OPTIONS,
      },
      {
        key: "todaysOfferStatsDisplayRoles",
        label: "Today's offer stats display roles",
        type: "enumList",
        options: USER_ROLE_AND_WILDCARD_OPTIONS,
      },
    ],
  },
  {
    id: "durations",
    title: "Durations (ms)",
    fields: [
      {
        key: "driverDeadheadEtaBufferMs",
        label: "Driver deadhead ETA buffer (ms)",
        type: "number",
      },
      { key: "loadExpireMs", label: "Load expire (ms)", type: "number" },
      {
        key: "driverOfferExpireMs",
        label: "Driver offer expire (ms)",
        type: "number",
      },
      {
        key: "dispatcherOfferExpireMs",
        label: "Dispatcher offer expire (ms)",
        type: "number",
      },
      {
        key: "dispatcherOfferExpireExtensionMs",
        label: "Dispatcher offer expire extension (ms)",
        type: "number",
      },
      {
        key: "driverHoldExpireDurationMs",
        label: "Driver hold expire duration (ms)",
        type: "number",
      },
      {
        key: "autoAssignLocationAgeMs",
        label: "Auto-assign location age (ms)",
        type: "number",
      },
      {
        key: "driverExtendOfferExpireTimeEnabled",
        label: "Driver extend offer expire time enabled",
        type: "boolean",
      },
    ],
  },
  {
    id: "auto-assign",
    title: "Auto-assign",
    fields: [
      {
        key: "autoAssignEnabled",
        label: "Auto-assign enabled",
        type: "boolean",
      },
      {
        key: "autoAssignToAcceptedDrivers",
        label: "Auto-assign to accepted drivers",
        type: "boolean",
      },
      {
        key: "autoAssignByDeliverLocation",
        label: "Auto-assign by deliver location",
        type: "boolean",
      },
      {
        key: "autoAssignCopyLoadNotesToOffer",
        label: "Copy load notes to offer",
        type: "boolean",
      },
      {
        key: "autoAssignToInTransitDrivers",
        label: "Auto-assign to in-transit drivers",
        type: "boolean",
      },
      {
        key: "autoAssignWhenNoTruckType",
        label: "Auto-assign when no truck type",
        type: "boolean",
      },
      {
        key: "autoAssignPriceNegotiable",
        label: "Auto-assign price negotiable",
        type: "boolean",
      },
      {
        key: "autoAssignOnHoldDrivers",
        label: "Auto-assign on-hold drivers",
        type: "boolean",
      },
      {
        key: "allowDriverSetAutoAssignRadius",
        label: "Allow driver set auto-assign radius",
        type: "boolean",
      },
      {
        key: "allowDriverSetAutoAssignMaxMiles",
        label: "Allow driver set auto-assign max miles",
        type: "boolean",
      },
      {
        key: "driverAutoAssignCanadaDefault",
        label: "Driver auto-assign Canada default",
        type: "boolean",
      },
      {
        key: "autoAssignDistanceMiles",
        label: "Auto-assign distance (miles)",
        type: "number",
      },
      {
        key: "maxAutoAssignDistanceMiles",
        label: "Max auto-assign distance (miles)",
        type: "number",
      },
      {
        key: "autoAssignMaxLoadMiles",
        label: "Auto-assign max load miles",
        type: "nullableNumber",
      },
      {
        key: "autoAssignInTransitDriverPercentage",
        label: "In-transit driver percentage",
        type: "number",
      },
      {
        key: "autoAssignDollarsCeilingStep",
        label: "Auto-assign dollars ceiling step",
        type: "number",
      },
    ],
  },
  {
    id: "pricing",
    title: "Pricing & profit",
    fields: [
      {
        key: "defaultDriverPricingModel",
        label: "Default driver pricing model",
        type: "enum",
        options: enumOptions(DefaultDriverPricingModel),
      },
      { key: "profitPercent", label: "Profit percent", type: "number" },
      {
        key: "profitDollarsCeilingStep",
        label: "Profit dollars ceiling step",
        type: "number",
      },
      {
        key: "minProfitDollarAmount",
        label: "Min profit dollar amount",
        type: "number",
      },
      {
        key: "costEnforcement",
        label: "Cost enforcement",
        type: "enum",
        options: enumOptions(CostEnforcement),
      },
      {
        key: "quickPayMode",
        label: "QuickPay mode",
        type: "enum",
        options: enumOptions(QuickPayMode),
      },
      {
        key: "quickPaySupported",
        label: "QuickPay supported",
        type: "boolean",
      },
      { key: "quickPayPercent", label: "QuickPay percent", type: "number" },
    ],
  },
  {
    id: "driver",
    title: "Driver settings",
    fields: [
      {
        key: "allowDriverChangePhoneNumber",
        label: "Allow driver change phone number",
        type: "boolean",
      },
      {
        key: "requireAddressToAcceptOffer",
        label: "Require address to accept offer",
        type: "boolean",
      },
      {
        key: "allowDriverRecordEntry",
        label: "Allow driver record entry",
        type: "boolean",
      },
      { key: "allowPpeEntry", label: "Allow PPE entry", type: "boolean" },
      {
        key: "allowLhNumberTransfers",
        label: "Allow LH number transfers",
        type: "boolean",
      },
      {
        key: "canadaLoadsEnabled",
        label: "Canada loads enabled",
        type: "boolean",
      },
      {
        key: "atPickupStatusEnabled",
        label: "At-pickup status enabled",
        type: "boolean",
      },
      {
        key: "atDropOffStatusEnabled",
        label: "At-dropoff status enabled",
        type: "boolean",
      },
      {
        key: "driverHoldOnReply",
        label: "Driver hold on reply",
        type: "enum",
        options: enumOptions(AutoHoldType),
      },
      {
        key: "driverMissingPaymentInfoDisplayLevel",
        label: "Missing payment info display level",
        type: "enum",
        options: enumOptions(MissingInfoDisplayLevel),
      },
      {
        key: "driverDefaultContractStatus",
        label: "Default contract status",
        type: "enum",
        options: enumOptions(ContractStatus),
      },
      {
        key: "driverAgreementFlow",
        label: "Driver agreement flow",
        type: "enum",
        options: enumOptions(DriverAgreementFlow),
      },
      {
        key: "driverAgreementClientUserId",
        label: "Driver agreement client user ID",
        type: "nullableText",
      },
      {
        key: "warningKeysRequiredForDriverAgreement",
        label: "Warning keys required for driver agreement",
        type: "stringList",
        hint: "Comma-separated",
      },
    ],
  },
  {
    id: "pickup-delivery",
    title: "Pickup / delivery entry",
    fields: [
      {
        key: "pickupAllowPhotoLibrary",
        label: "Pickup allow photo library",
        type: "boolean",
      },
      {
        key: "dropOffAllowPhotoLibrary",
        label: "Drop-off allow photo library",
        type: "boolean",
      },
      {
        key: "pickupAllowShipperNameEntry",
        label: "Pickup allow shipper name entry",
        type: "boolean",
      },
      {
        key: "allowTruckSideDoorEntry",
        label: "Allow truck side door entry",
        type: "boolean",
      },
      {
        key: "allowTruckBackDoorEntry",
        label: "Allow truck back door entry",
        type: "boolean",
      },
      {
        key: "allowTruckMcNumberEntry",
        label: "Allow truck MC number entry",
        type: "boolean",
      },
      {
        key: "offerPodIncludeCoverPageDefault",
        label: "POD include cover page default",
        type: "boolean",
      },
    ],
  },
  {
    id: "subscriptions",
    title: "Offer subscriptions",
    fields: [
      {
        key: "offerSubscriptionsAutoEmailsEnabled",
        label: "Auto emails enabled",
        type: "boolean",
      },
      {
        key: "offerSubscriptionsOnAcceptedDefault",
        label: "On accepted default",
        type: "boolean",
      },
      {
        key: "offerSubscriptionsOnAtPickupDefault",
        label: "On at-pickup default",
        type: "boolean",
      },
      {
        key: "offerSubscriptionsOnInTransitDefault",
        label: "On in-transit default",
        type: "boolean",
      },
      {
        key: "offerSubscriptionsOnAtDropOffDefault",
        label: "On at-dropoff default",
        type: "boolean",
      },
      {
        key: "offerSubscriptionsOnDeliveredDefault",
        label: "On delivered default",
        type: "boolean",
      },
    ],
  },
];
